import React, { useEffect, useState, createContext, useContext, memo } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, User as FirebaseUser, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { createUser, getUserById, updateUser, deleteUser as deleteFirestoreUser } from '../../firebase/auth';
interface User {
  id: string;
  name: string | null;
  email: string | null;
  isSeller: boolean;
  roles?: string[];
  activeRole: 'seller' | 'buyer';
}
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<User>;
  register: (userData: Omit<User, 'id'>, password: string) => Promise<User>;
  logout: () => Promise<void>;
  switchRole: (role: 'buyer' | 'seller') => Promise<void>;
  hasRole: (role: string) => boolean;
  addRole: (role: string) => Promise<void>;
  needsVerification: boolean;
  loading: boolean;
  isLoggingOut: boolean;
  demoLogin?: (userData: User) => Promise<User>; // Optional demo login function
}
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: async () => ({
    id: '',
    name: '',
    email: '',
    roles: [],
    activeRole: 'buyer',
    isSeller: false
  }),
  register: async () => ({
    id: '',
    name: '',
    email: '',
    roles: [],
    activeRole: 'buyer',
    isSeller: false
  }),
  logout: async () => {},
  switchRole: async () => {},
  hasRole: () => false,
  addRole: async () => {},
  needsVerification: false,
  loading: true,
  isLoggingOut: false
});
export const useAuth = () => useContext(AuthContext);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Add safer environment variable check with fallback
  const isDemoLoginEnabled = (() => {
    try {
      if (typeof import.meta === 'undefined' || !import.meta.env) {
        return false;
      }
      return import.meta.env.VITE_ENABLE_DEMO_LOGIN === 'true';
    } catch {
      return false;
    }
  })();
  // Add a safety timeout to prevent infinite loading
  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);
    return () => clearTimeout(loadingTimeout);
  }, [loading]);
  // Initialize auth with explicit persistence
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(error => {
      console.error('Error setting auth persistence:', error);
    });
  }, []);
  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (firebaseUser) {
        // User is signed in
        try {
          const firestoreUser = await getUserById(firebaseUser.uid);
          if (firestoreUser) {
            const userData: User = {
              id: firebaseUser.uid,
              name: firestoreUser.name,
              email: firestoreUser.email,
              roles: firestoreUser.roles || [firestoreUser.role],
              activeRole: firestoreUser.activeRole || firestoreUser.role as 'buyer' | 'seller',
              isSeller: firestoreUser.role === 'seller' || firestoreUser.roles?.includes('seller')
            };
            setUser(userData);
            setIsAuthenticated(true);
            setNeedsVerification(firestoreUser.role === 'seller' || firestoreUser.roles?.includes('seller'));
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(null);
          setIsAuthenticated(false);
          setNeedsVerification(false);
        }
      } else {
        // User is signed out
        setUser(null);
        setIsAuthenticated(false);
        setNeedsVerification(false);
      }
      // Always set loading to false when auth state is determined
      setLoading(false);
    });
    // Cleanup subscription
    return () => {
      unsubscribe();
    };
  }, []);
  const register = async (userData: Omit<User, 'id'>, password: string): Promise<User> => {
    // Step 1: Create Firebase Auth user (required — will throw on failure)
    const userCredential = await createUserWithEmailAndPassword(auth, userData.email, password);
    const firebaseUser = userCredential.user;
    const roles = ['buyer', 'seller'];
    const userType = userData.activeRole === 'seller' ? 'seller' : 'buyer';
    // Step 2: Write user profile to Firestore (non-blocking)
    // If Firestore rules aren't deployed yet, signup still succeeds
    // The profile will be created on next login or can be retried
    try {
      await createUser({
        name: userData.name,
        email: userData.email,
        role: userType,
        roles: roles,
        activeRole: userData.activeRole || 'buyer'
      }, firebaseUser.uid);
    } catch (firestoreError) {
      // Don't throw — the Auth user was created successfully
    }
    const newUser: User = {
      id: firebaseUser.uid,
      ...userData,
      roles,
      isSeller: roles.includes('seller')
    };
    return newUser;
  };
  const login = async (email: string, password: string, rememberMe?: boolean): Promise<User> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      const firestoreUser = await getUserById(firebaseUser.uid);
      if (!firestoreUser) {
        throw new Error('User data not found in Firestore');
      }
      const userData: User = {
        id: firebaseUser.uid,
        name: firestoreUser.name,
        email: firestoreUser.email,
        roles: firestoreUser.roles || [firestoreUser.role],
        activeRole: firestoreUser.activeRole || firestoreUser.role as 'buyer' | 'seller',
        isSeller: firestoreUser.role === 'seller' || firestoreUser.roles?.includes('seller')
      };
      return userData;
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  };
  // Demo login function - only available if enabled via environment variable
  const demoLogin = async (userData: User): Promise<User> => {
    if (!isDemoLoginEnabled) {
      console.error('Demo login is disabled. Set VITE_ENABLE_DEMO_LOGIN=true to enable.');
      throw new Error('Demo login is disabled');
    }
    setIsAuthenticated(true);
    setUser(userData);
    return userData;
  };
  const logout = async () => {
    try {
      // Set logging out flag FIRST — this prevents ProtectedRoute from showing 404
      setIsLoggingOut(true);
      // Navigate immediately — no delay, no setTimeout
      window.location.href = '/';
      // Now clean up in the background (page is already navigating away)
      const savedTheme = localStorage.getItem('theme');
      if (auth.currentUser) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error('Firebase signOut failed:', signOutError);
        }
      }
      setIsAuthenticated(false);
      setUser(null);
      setNeedsVerification(false);
      // Clear localStorage (except theme, demo purchases, and last dashboard)
      Object.keys(localStorage).forEach(key => {
        if (key === 'theme' || key === 'dgstack_last_dashboard' || key.startsWith('dgstack_demo_purchases_')) {
          return;
        }
        localStorage.removeItem(key);
      });
      if (savedTheme) {
        localStorage.setItem('theme', savedTheme);
      }
      sessionStorage.clear();
      clearAllCookies();
    } catch (error) {
      console.error('Error during logout:', error);
      // Force navigate even on error
      window.location.href = '/';
    }
  };
  const switchRole = async (role: 'buyer' | 'seller') => {
    if (!user) {
      throw new Error('Cannot switch role: User not authenticated');
    }
    try {
      // Update the user's active role in Firestore
      await updateUser(user.id, {
        activeRole: role
      });
      // Update local state
      setUser({
        ...user,
        activeRole: role
      });
      return role;
    } catch (error) {
      console.error('Error switching role:', error);
      throw error;
    }
  };
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  };
  const addRole = async (role: string) => {
    if (!user) return;
    // Skip if user already has this role
    if (user.roles?.includes(role)) return;
    try {
      // Add the new role to the user's roles
      const updatedRoles = [...(user.roles || []), role];
      const updatedUser = {
        ...user,
        roles: updatedRoles,
        // Update other role-related fields
        isSeller: role === 'seller' || user.isSeller
      };
      // Update in Firestore
      await updateUser(user.id, {
        roles: updatedRoles
      });
      // Update state
      setUser(updatedUser);
    } catch (error) {
      console.error('Error adding role:', error);
      throw error;
    }
  };
  const clearAllCookies = () => {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  };
  // Check for logout flag and clear it
  useEffect(() => {
    const loggingOut = sessionStorage.getItem('logging_out');
    if (loggingOut) {
      sessionStorage.removeItem('logging_out');
      setLoading(false);
    }
  }, []);
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen" data-id="element-205">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-206"></div>
      </div>;
  }
  return <AuthContext.Provider value={{
    isAuthenticated,
    user,
    login,
    register,
    logout,
    switchRole,
    hasRole,
    addRole,
    needsVerification,
    loading,
    isLoggingOut,
    ...(isDemoLoginEnabled ? {
      demoLogin
    } : {}) // Only include demoLogin if enabled
  }} data-id="element-207">
      {children}
    </AuthContext.Provider>;
};